import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../apiClient";
import { Link } from "react-router-dom";
import { formatMoney } from "../formatMoney";

const Banks = () => {
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: "Description", direction: "asc" });

    const depositTotals = useMemo(() => {
        const totals = banks.reduce((acc, bank) => {
            if (bank.LatestBalanceAmount == null) return acc;
            const currency = bank.CurrencyDescription || "Unspecified";
            acc[currency] = (acc[currency] || 0) + Number(bank.LatestBalanceAmount || 0);
            return acc;
        }, {});
        return Object.entries(totals).map(([currency, total]) => ({ currency, total }));
    }, [banks]);

    const filteredBanks = useMemo(() => {
      const query = search.trim().toLowerCase();
      const filtered = !query ? banks : banks.filter((bank) =>
        [bank.Description, bank.CurrencyDescription, bank.LatestBalanceDate, Number(bank.IsRetirement) ? "retirement" : ""]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query))
      );
      return [...filtered].sort((left, right) => {
        const leftValue = left[sortConfig.key];
        const rightValue = right[sortConfig.key];
        const leftNumeric = Number(leftValue);
        const rightNumeric = Number(rightValue);
        const comparison = Number.isFinite(leftNumeric) && Number.isFinite(rightNumeric)
          ? leftNumeric - rightNumeric
          : String(leftValue || "").localeCompare(String(rightValue || ""));
        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    }, [banks, search, sortConfig]);

    const handleSort = (key) => {
      setSortConfig((current) => (
        current.key === key
          ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
          : { key, direction: "asc" }
      ));
    };

    useEffect (() => {
        const fetchAllBanks = async () => {
            try {
                const res = await apiClient.get("/banks");
                setBanks(res.data);
            } catch (err) {
                setError("Failed to load banks.");
            } finally {
                setLoading(false);
            }
        };
        fetchAllBanks();
    }, []);

    const handleDelete = async (bank) => {
        if (!window.confirm(`Are you sure you wish to delete ${bank.Description} bank?`)) return;

        try {
          await apiClient.delete(`/banks/${bank.BankID}`);
          setBanks((prev) => prev.filter((item) => item.BankID !== bank.BankID));
        } catch (err) {
          setError("Delete failed. Please try again.");
        }
      };

    return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Banks</h1>
          <p className="page-subtitle"><Link to="/">Home</Link> / Banks</p>
        </div>
        <div className="actions">
          <Link className="btn-link" to="/BankBalances">Enter Balances</Link>
          <Link className="btn-link" to="/BanksAdd">Add Bank</Link>
        </div>
      </header>

      {error && <p className="notice notice-error">{error}</p>}
      {loading && <p className="notice notice-muted">Loading banks...</p>}

      {!loading && (
        <section className="resource-list">
          {banks.length === 0 ? (
            <article className="panel empty-state">
              <h2>No banks yet</h2>
              <p className="notice notice-muted">Add your first bank account before entering balances, passive income, or net worth data.</p>
              <div className="form-actions">
                <Link className="btn" to="/BanksAdd">Add First Bank</Link>
              </div>
            </article>
          ) : (
            <>
              <article className="panel">
                <h2>Total Deposits</h2>
                <div className="dashboard-grid">
                  {depositTotals.map(({ currency, total }) => (
                    <div key={currency} className="metric-card metric-card-compact">
                      <strong>{currency}</strong>
                      <div className="resource-meta">{formatMoney(total, currency)}</div>
                    </div>
                  ))}
                </div>
              </article>

              <section className="panel page-cta list-filter-panel">
                <div className="list-filters">
                  <label className="list-filter-field">
                    <span>Search</span>
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Find a bank"
                    />
                  </label>
                </div>
              </section>

              <div className="table-scroll">
                <table className="resource-table">
                  <thead>
                    <tr>
                      <th><button type="button" className="table-sort" onClick={() => handleSort("Description")}>Bank</button></th>
                      <th><button type="button" className="table-sort" onClick={() => handleSort("CurrencyDescription")}>Currency</button></th>
                      <th className="table-number"><button type="button" className="table-sort table-sort-number" onClick={() => handleSort("IsRetirement")}>Retirement</button></th>
                      <th className="table-number"><button type="button" className="table-sort table-sort-number" onClick={() => handleSort("LatestBalanceAmount")}>Latest Balance</button></th>
                      <th className="table-number"><button type="button" className="table-sort table-sort-number" onClick={() => handleSort("AnnualInterestRate")}>Interest Rate</button></th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBanks.map((bank) => (
                      <tr key={bank.BankID}>
                        <td data-label="Bank"><strong>{bank.Description}</strong></td>
                        <td data-label="Currency">{bank.CurrencyDescription || "-"}</td>
                        <td data-label="Retirement" className="table-number">
                          <span className={`status-check${Number(bank.IsRetirement) ? " status-check-on" : ""}`} aria-label={Number(bank.IsRetirement) ? "Retirement bank" : "Not retirement"}>
                            {Number(bank.IsRetirement) ? "✓" : "-"}
                          </span>
                        </td>
                        <td data-label="Latest Balance" className="table-number table-money">
                          {bank.LatestBalanceAmount == null ? "-" : formatMoney(bank.LatestBalanceAmount, bank.CurrencyDescription)}
                          {bank.LatestBalanceDate ? <div className="resource-meta">{bank.LatestBalanceDate}</div> : null}
                        </td>
                        <td data-label="Interest Rate" className="table-number table-money">{Number(bank.AnnualInterestRate || 0).toFixed(2)}%</td>
                        <td data-label="Actions" className="resource-table-actions">
                          <Link className="btn-link" to={`/BanksUpdate/${bank.BankID}`}>Edit</Link>
                          <Link className="action-link" to={`/BankBalances?bankId=${bank.BankID}`}>Balances</Link>
                          <button className="action-link action-link-danger" onClick={() => handleDelete(bank)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}
    </div>
    );
};

export default Banks;
